/**
 * Party Import
 */

import { uniqueId } from 'lodash'

import { axiosRequest } from '@/plugins/axios-common'
import { handleError } from '@/store/handle-error'

const IMPORT_PARTY_SCHEMA = {
  aNum: null,
  akaName: null,
  akaNotes: null,
  cityOfBirth: null,
  countryOfBirth: null,
  deceased: null,
  demographicReligion: null,
  demographicRace: null,
  demographicEthnicity: null,
  demographicHouseholdIncome: null,
  demographicHouseholdSize: null,
  dob: null,
  emailAddress: null,
  emailOwner: null,
  firstName: null,
  forceCreationComment: null,
  gender: null,
  identifierAnum: null,
  identifierSsn: null,
  identifierNotes: null,
  lastName: null,
  narrativeDate: null,
  narrativeTakenBy: null,
  narrativeText: null,
  nationalities: null,
  mailingAddressCity: null,
  mailingAddressCountry: null,
  mailingAddressCounty: null,
  mailingAddressState: null,
  mailingAddressStreetAddress: null,
  mailingAddressZip: null,
  middleName: null,
  passportCountry: null,
  passportNumber: null,
  passportDateIssued: null,
  passportPlaceIssued: null,
  passportDateOfExpiry: null,
  passportNotes: null,
  phoneNumber: null,
  phoneExtension: null,
  phoneOwner: null,
  physicalAddressCity: null,
  physicalAddressCountry: null,
  physicalAddressCounty: null,
  physicalAddressState: null,
  physicalAddressStreetAddress: null,
  physicalAddressZip: null,
  primaryLanguage: null,
  ssn: null,
  stateOfBirth: null,
}

const IMPORT_PARTY_UPLOADED = {
  headers: [],
  data: [],
}

export const IMPORT_PARTY_NAMESPACE = 'importParty'

const ImportPartyState = {
  importedParties: [],
  importedPartiesDialog: false,
  importedPartiesItemsPerPage: 15,
  importedPartiesPageCount: 1,
  importPartyCompletedDialog: false,
  importPartyHeaders: null,
  importPartyOverlay: false,
  importPartySchema: IMPORT_PARTY_SCHEMA,
  importPartyTransformedUpload: [],
  importPartyUploaded: IMPORT_PARTY_UPLOADED,
  importPartyValidateErrors: [],
  importPartyValidateErrorsDialog: false,
  importPartyValidateErrorsHeaders: [],
  importPartyValidateErrorsPageCount: 1,
}

const ImportPartyActions = {
  importParties: async ({ commit, getters }) => {
    commit('setImportPartyOverlay', true)

    return axiosRequest.
      post('/parties/imports/new', { parties: getters.importPartyTransformedUpload }).
      then(({ data }) => {
        commit('setImportedParties', data.parties)
        commit('setImportedPartiesPageCount', Math.ceil(data.parties.length / getters.importedPartiesItemsPerPage))
        commit('setImportPartyOverlay', false)
        commit('setImportPartyCompletedDialog', true)
      }).
      catch(handleError)
  },
  importPartyValidateTransformedParties: async (_, req) => {
    return axiosRequest.
      post('/parties/imports/validate', { parties: req }).
      then(({ data }) => data).
      catch(handleError)
  },
}

const ImportPartyMutations = {
  setImportedParties: (state, payload) => { state.importedParties = payload || [] },
  setImportedPartiesDialog: (state, payload) => { state.importedPartiesDialog = payload },
  setImportedPartiesPageCount: (state, payload) => { state.importedPartiesPageCount = payload },
  setImportPartyCompletedDialog: (state, payload) => { state.importPartyCompletedDialog = payload },
  setImportPartyHeaders: (state, payload) => { state.importPartyHeaders = payload },
  setImportPartyOverlay: (state, payload) => { state.importPartyOverlay = payload },
  setImportPartySchema: (state, payload) => {
    if (!payload) payload = IMPORT_PARTY_SCHEMA

    state.importPartySchema = payload
  },
  setImportPartyTransformedUpload: (state, payload) => { state.importPartyTransformedUpload = payload || [] },
  setImportPartyUploaded: (state, payload) => {
    if (!payload) {
      payload = {
        data: [],
        headers: [],
      }
    }

    payload.data = payload.data.map(item => ({ ...item, reference: uniqueId() }))

    state.importPartyUploaded = payload
  },
  setImportPartyValidateErrors: (state, payload) => { state.importPartyValidateErrors = payload },
  setImportPartyValidateErrorsDialog: (state, payload) => { state.importPartyValidateErrorsDialog = payload },
  setImportPartyValidateErrorsHeaders: (state, payload) => {
    let keys = payload.map(err => Object.keys(err))
    keys.unshift('row_id')
    keys = [...new Set(keys.flat())]

    state.importPartyValidateErrorsHeaders = keys.map(key => {
      const header = {
        text: key,
        value: key,
      }
      if (key === 'row_id') header.align = 'start'
      return header
    })
  },
  setImportPartyValidateErrorsPageCount: (state, payload) => { state.importPartyValidateErrorsPageCount = payload },
}

const ImportPartyGetters = {
  importedParties: state => state.importedParties,
  importedPartiesDialog: state => state.importedPartiesDialog,
  importedPartiesItemsPerPage: state => state.importedPartiesItemsPerPage,
  importedPartiesPageCount: state => state.importedPartiesPageCount,
  importPartyCompletedDialog: state => state.importPartyCompletedDialog,
  importPartyCount: state => state.importPartyUploaded.data.length,
  importPartyHeaders: state => state.importPartyHeaders,
  importPartyOverlay: state => state.importPartyOverlay,
  importPartySchema: state => state.importPartySchema,
  importPartyTransformedUpload: state => state.importPartyTransformedUpload,
  importPartyUploaded: state => state.importPartyUploaded,
  importPartyValidateErrors: state => state.importPartyValidateErrors,
  importPartyValidateErrorsDialog: state => state.importPartyValidateErrorsDialog,
  importPartyValidateErrorsHeaders: state => state.importPartyValidateErrorsHeaders,
  importPartyValidateErrorsPageCount: state => state.importPartyValidateErrorsPageCount,
}

export const ImportParty = {
  namespaced: true,
  state: ImportPartyState,
  actions: ImportPartyActions,
  mutations: ImportPartyMutations,
  getters: ImportPartyGetters,
}
